import { RefObject, useEffect } from "react";

// Shared row-selection plumbing for the Material React Table grids.
//
// The grids used to each hand-roll this: a ~100-line `if (context === ...)`
// chain that hard-coded one id field per context, an `index = 0` default that
// silently selected the FIRST row for any context the chain had forgotten, and
// an unguarded `findIndex` result that wrote `{ "-1": true }` (nothing
// highlighted) plus "Record 0 of N" when the record wasn't in the loaded list.
// Worst of all, the effect that re-applied the selection depended only on the
// route param, so on a page refresh -- where the id is known on the first
// render but the list arrives later -- it ran once against an empty list and
// never ran again, losing the selection entirely.

// The field that identifies one record, per table context. This is the single
// place that knowledge lives now; adding a context here is all that's needed
// for its selection to survive a refresh.
export const CONTEXT_ID_KEYS: Record<string, string> = {
  // Client
  CLIENT_SERVICE_TYPE: "servicetype_id",
  CLIENT_OBJECTIVES: "objective_id",
  CLIENT_IDENTITY: "identity_id",
  CLIENT_EMPLOYMENT: "employment_id",
  CLIENT_DEPENDANT: "dependant_id",
  CLIENT_INCOME: "income_id",
  CLIENT_OUTGOINGS: "expense_id",
  CLIENT_ATTITUDE_TO_RISK: "attituderisk_id",
  CLIENT_HISTORY: "note_id",
  CLIENT_VULNERABILITY: "vulnerability_id",
  ADDRESS_BOOK: "address_id",
  FACT_FIND_NOTES: "note_id",
  FEES_CHARGES: "commission_id",
  VULNERABILITY: "vulnerability_id",
  // Contacts
  CONTACTS_NOTES: "note_id",
  CONTACTS_TIME_ALLOCATION: "timeallocation_id",
  CONTACTS_CLIENT_ACTION: "appointment_id",
  // Partner
  PARTNER_SERVICE_TYPE: "servicetype_id",
  PARTNER_OBJECTIVES: "objective_id",
  PARTNER_IDENTITY: "identity_id",
  PARTNER_EMPLOYMENT: "employment_id",
  PARTNER_DEPENDANT: "dependant_id",
  PARTNER_INCOME: "income_id",
  PARTNER_OUTGOINGS: "expense_id",
  PARTNER_ATTITUDE_TO_RISK: "attituderisk_id",
  PARTNER_VULNERABILITY: "vulnerability_id",
  // Commission
  COMMISSION_SERVICE_TYPE: "servicetype_id",
  COMMISSION_PARTNER_SERVICE_TYPE: "servicetype_id",
  COMMISSION_TYPES: "id",
  COMMISSION_RULES: "id",
  // System Manager
  SYSTEM_MANAGER_USERLIST: "user_id",
  MASTER_SETTINGS_INCOME_CATEGORY: "id",
  MASTER_SETTINGS_EXPENSE_CATEGORY: "id",
  MASTER_SETTINGS_ATTITUDE_TO_RISK_CATEGORY: "id",
  MASTER_SETTINGS_ATTITUDE_TO_RISK_RATING: "id",
  MASTER_SETTINGS_OBJECTIVES: "id",
  MASTER_SETTINGS_USER_DEFINED: "id",
  MASTER_SETTINGS_PROVIDERS: "id",
  MASTER_SETTINGS_CASE_ACTIONS: "id",
  MASTER_SETTINGS_CLIENT_ACTIONS: "id",
};

export function getContextIdKey(context?: string): string | undefined {
  return context ? CONTEXT_ID_KEYS[context] : undefined;
}

// The key MRT stores a row under in `rowSelection`. Must stay in sync with the
// grid's `getRowId`, which is why both call this. Falls back to the row index
// for contexts with no mapped id field -- that is also what MRT itself does
// when `getRowId` returns undefined, so unmapped contexts keep working exactly
// as they did before.
export function getRowKey(row: any, index: number, idKey?: string): string {
  const id = idKey ? row?.[idKey] : undefined;
  return id === undefined || id === null || id === "" ? String(index) : String(id);
}

// Find the row a `rowSelection` key points at. Tries the business id first and
// falls back to reading the key as an index, so it resolves both id-keyed rows
// and the index-keyed rows of unmapped contexts.
export function findRowIndexByKey(
  data: any[] | undefined,
  key: string | undefined,
  idKey?: string,
): number {
  if (!Array.isArray(data) || key === undefined) return -1;
  if (idKey) {
    const index = data.findIndex((row) => String(row?.[idKey]) === String(key));
    if (index !== -1) return index;
  }
  if (/^\d+$/.test(key)) {
    const index = Number(key);
    return index < data.length ? index : -1;
  }
  return -1;
}

// Find a record by its id. Compared as strings because route params are always
// strings while the ids in the list data may be numbers -- a `===` comparison
// between the two silently never matches.
export function findRowIndexById(
  data: any[] | undefined,
  selectedId: string | number | null | undefined,
  idKey?: string,
): number {
  if (!Array.isArray(data) || !idKey) return -1;
  if (selectedId === undefined || selectedId === null || selectedId === "") {
    return -1;
  }
  return data.findIndex((row) => String(row?.[idKey]) === String(selectedId));
}

// Bring the highlighted row into view. Restoring the selection only paints the
// highlight -- on a long list the record can sit below the fold, which reads to
// the user as "my selection was lost" even though it wasn't.
export function scrollSelectedRowIntoView(
  containerRef?: RefObject<HTMLElement>,
): void {
  if (!containerRef?.current || typeof window === "undefined") return;
  const scroll = () => {
    const row = containerRef.current?.querySelector<HTMLElement>(
      "tr.Mui-selected",
    );
    // Guard the call itself: jsdom (and older browsers) don't implement it.
    if (row && typeof row.scrollIntoView === "function") {
      row.scrollIntoView({ block: "nearest" });
    }
  };
  // One frame later, so MRT has re-rendered the row with its selected class.
  if (typeof window.requestAnimationFrame === "function") {
    window.requestAnimationFrame(scroll);
  } else {
    setTimeout(scroll, 0);
  }
}

interface UseRestoreRowSelectionParams {
  // The rows currently rendered. Included as an effect dependency, so the
  // selection is re-applied when the list arrives or is refetched -- the part
  // an id-only dependency array misses on a page refresh.
  data: any[] | undefined;
  idKey?: string;
  // The record that should be selected: normally a route param.
  selectedId?: string | number | null;
  rowSelection: Record<string, boolean>;
  setRowSelection: (selection: Record<string, boolean>) => void;
  setCurrentRecord?: (record: number) => void;
  // Called just before a programmatic selection is applied, so the grid can
  // tell it apart from a user's click and skip firing its row-action callback
  // (which navigates, and would otherwise turn an `/edit` URL into `/view`
  // and push a history entry on every refresh).
  onBeforeRestore?: () => void;
  containerRef?: RefObject<HTMLElement>;
}

export function useRestoreRowSelection({
  data,
  idKey,
  selectedId,
  rowSelection,
  setRowSelection,
  setCurrentRecord,
  onBeforeRestore,
  containerRef,
}: UseRestoreRowSelectionParams): void {
  useEffect(() => {
    const index = findRowIndexById(data, selectedId, idKey);
    // Not in the loaded list (wrong page, deleted, or no id in the URL yet).
    // Leave whatever is selected alone rather than selecting row 0 or writing
    // a "-1" key that highlights nothing and reports "Record 0 of N".
    if (index < 0) return;

    const key = getRowKey((data as any[])[index], index, idKey);
    setCurrentRecord?.(index + 1);
    if (rowSelection[key]) return; // already selected -- nothing to re-apply

    onBeforeRestore?.();
    setRowSelection({ [key]: true });
    scrollSelectedRowIntoView(containerRef);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, idKey, selectedId]);
}
